import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Confetti, Sparkle } from "@phosphor-icons/react";

/**
 * Triggers a celebration toast when goal % crosses a new milestone (25/50/75/100).
 * Stores last shown threshold per goalKey in sessionStorage to prevent re-triggers
 * during the same session.
 */
export default function MilestoneCelebrator({ pct, goalKey }) {
    const [milestone, setMilestone] = useState(null);
    const seen = useRef(false);

    useEffect(() => {
        if (seen.current) return;
        const key = `cf-milestone-${goalKey}`;
        const last = parseInt(sessionStorage.getItem(key) || "0", 10);
        const thresholds = [25, 50, 75, 100];
        const reached = thresholds.filter(t => pct >= t).pop() || 0;
        if (reached > last) {
            sessionStorage.setItem(key, String(reached));
            setMilestone(reached);
            seen.current = true;
            setTimeout(() => setMilestone(null), 6000);
        }
    }, [pct, goalKey]);

    return (
        <AnimatePresence>
            {milestone && (
                <motion.div
                    initial={{ y: -100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -100, opacity: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="fixed top-24 left-1/2 -translate-x-1/2 z-50 bg-accent text-accent-foreground px-8 py-6 shadow-2xl flex items-center gap-4"
                    data-testid={`milestone-${milestone}`}
                >
                    <Sparkle size={36} weight="fill" />
                    <div>
                        <p className="label-eyebrow text-accent-foreground/80">Milestone unlocked</p>
                        <p className="font-heading text-3xl mt-1">
                            {milestone === 100 ? "Goal reached! 🎉" : `${milestone}% of goal raised`}
                        </p>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
